import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { useAlert, useConfirm } from "../../components/Dialog";
import { useAuth } from "../../context/AuthContext";
import { useMarket } from "../../context/MarketContext";
import {
  PAYMENT_METHOD_LABEL,
  PAYMENT_METHOD_STYLES,
  PaymentMethodType,
  UNPAID_STATUS_LABEL,
  UNPAID_STATUS_STYLES,
  UnpaidInvoiceRow,
  UnpaidInvoicesResponse,
  UnpaidStatus,
} from "../../types/impagos";
import { LeadStatus } from "../../types/onboarding";
import Pagination from "../OnboardingCustomers/components/Pagination";
import AggregatesBar from "./components/AggregatesBar";
import FiltersBar from "./components/FiltersBar";
import ImpagoActionsMenu from "./components/ImpagoActionsMenu";

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// Same fallback shape as api/http.ts. Inlined here (instead of exporting
// BASE_URL) for consistency with BrokerInvoicesTab and DocumentDownload,
// which also bypass the JSON request() helper to fetch binary downloads.
const BASE_URL =
  process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") ??
  "http://localhost:8000";

const fmtMoney = (n: number | null): string => {
  if (n === null) return "—";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const ageColor = (age: number | null): string => {
  if (age === null) return "#73879C";
  if (age <= 7) return "#155724";
  if (age <= 30) return "#856404";
  if (age <= 90) return "#b32424";
  return "#7c1d1d";
};

export default function Impagos() {
  const { token } = useAuth();
  const { markets } = useMarket();
  const alert = useAlert();
  const confirm = useConfirm();
  const [data, setData] = useState<UnpaidInvoicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [carrier, setCarrier] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<UnpaidStatus[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodType[]>([]);
  const [minAge, setMinAge] = useState("");
  const [maxAge, setMaxAge] = useState("");
  const [minLeadUnpaidCount, setMinLeadUnpaidCount] = useState("");
  const [leadStatuses, setLeadStatuses] = useState<LeadStatus[]>([]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<
    null | "regenerate" | "conciliate" | "cancel"
  >(null);
  const [cancelFormOpen, setCancelFormOpen] = useState(false);
  const [cancelRequestDate, setCancelRequestDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [cancelEffectiveDate, setCancelEffectiveDate] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Filter portion of the query string, shared between the paginated list
  // fetch and the CSV export so both endpoints always reflect the same
  // user-chosen filters. Pagination (limit/offset) is added by the list
  // call only — the export endpoint applies its own EXPORT_LIMIT cap.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    selectedStatuses.forEach((s) => params.append("status[]", s));
    paymentMethods.forEach((m) => params.append("paymentMethod[]", m));
    leadStatuses.forEach((s) => params.append("leadStatus[]", s));
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (carrier) params.set("carrier", carrier);
    if (minAge) params.set("minAgeDays", minAge);
    if (maxAge) params.set("maxAgeDays", maxAge);
    if (minLeadUnpaidCount) params.set("minLeadUnpaidCount", minLeadUnpaidCount);
    return params;
  }, [
    selectedStatuses,
    paymentMethods,
    leadStatuses,
    debouncedSearch,
    carrier,
    minAge,
    maxAge,
    minLeadUnpaidCount,
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = buildFilterParams();
    params.set("limit", String(pageSize));
    params.set("offset", String(page * pageSize));
    try {
      const response = await request<UnpaidInvoicesResponse>(
        `/api/admin/operations/unpaid-invoices?${params.toString()}`,
        { token, markets },
      );
      setData(response);
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [token, markets, buildFilterParams, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const carriers = useMemo(
    () =>
      Array.from(
        new Set((data?.rows ?? []).map((r) => r.carrier).filter(Boolean) as string[]),
      ).sort(),
    [data],
  );

  const toggleStatus = (s: UnpaidStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
    setPage(0);
  };

  const togglePaymentMethod = (m: PaymentMethodType) => {
    setPaymentMethods((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
    );
    setPage(0);
  };

  const toggleLeadStatus = (s: LeadStatus) => {
    setLeadStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
    setPage(0);
  };

  const clearFilters = () => {
    setSearch("");
    setCarrier("");
    setSelectedStatuses([]);
    setPaymentMethods([]);
    setMinAge("");
    setMaxAge("");
    setMinLeadUnpaidCount("");
    setLeadStatuses([]);
    setPage(0);
  };

  const handleExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    setError(null);
    try {
      const params = buildFilterParams();
      // Bypass the JSON request() helper because the endpoint streams a
      // text/csv binary body; same pattern used by the broker invoices
      // and lead document download flows.
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (markets && markets.length > 0) {
        headers["X-Market"] = markets.join(",");
      }
      const response = await fetch(
        `${BASE_URL}/api/admin/operations/unpaid-invoices/export?${params.toString()}`,
        { headers },
      );
      if (!response.ok) {
        throw new HttpError(response.status, await response.text());
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Server sets Content-Disposition with the timestamped name but it's
      // not exposed via the Blob path — synthesize a matching fallback so
      // the download is recognisable in the user's Downloads folder.
      const stamp = new Date()
        .toISOString()
        .slice(0, 16)
        .replace(/[-T:]/g, "")
        .replace(/(\d{8})(\d{4})/, "$1-$2");
      const a = document.createElement("a");
      a.href = url;
      a.download = `unpaid-invoices-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status} al exportar`
          : "No se pudo exportar",
      );
    } finally {
      setExporting(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (!data) return;
    if (selected.size === data.rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.rows.map((r) => r.invoiceId)));
    }
  };

  const selectedUniqueLeadUlids = useMemo(() => {
    if (!data) return [] as string[];
    const set = new Set<string>();
    for (const row of data.rows) {
      if (selected.has(row.invoiceId) && row.leadUlid) {
        set.add(row.leadUlid);
      }
    }
    return Array.from(set);
  }, [data, selected]);

  const runBulkCancel = async () => {
    if (bulkBusy || selectedUniqueLeadUlids.length === 0) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(cancelRequestDate)) {
      await alert({
        title: "Fecha de solicitud requerida",
        message: "Indica una fecha de solicitud válida (YYYY-MM-DD).",
      });
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(cancelEffectiveDate)) {
      await alert({
        title: "Fecha efectiva requerida",
        message: "Indica una fecha efectiva válida (YYYY-MM-DD).",
      });
      return;
    }
    const ok = await confirm({
      title: "Cancelar leads por impago",
      message:
        `Se cancelarán ${selectedUniqueLeadUlids.length} lead(s) único(s)`
        + ` con razón UNPAID_INVOICES.`
        + ` Solicitud: ${cancelRequestDate}. Efectiva: ${cancelEffectiveDate}.`,
      confirmLabel: `Cancelar (${selectedUniqueLeadUlids.length})`,
      tone: "danger",
    });
    if (!ok) return;
    setBulkBusy("cancel");
    let failures = 0;
    for (const leadUlid of selectedUniqueLeadUlids) {
      try {
        await request(`/api/admin/operations/leads/${leadUlid}/cancel`, {
          method: "POST",
          token,
          body: {
            reason: "UNPAID_INVOICES",
            cancellationRequest: cancelRequestDate,
            effectiveDate: cancelEffectiveDate,
          },
        });
      } catch {
        failures++;
      }
    }
    setBulkBusy(null);
    setCancelFormOpen(false);
    setSelected(new Set());
    await load();
    if (failures > 0) {
      setError(`${failures} lead(s) fallaron al cancelar`);
    }
  };

  const runBulk = async (
    kind: "regenerate" | "conciliate",
    path: (row: UnpaidInvoiceRow) => string,
    filter?: (row: UnpaidInvoiceRow) => boolean,
  ) => {
    if (!data || bulkBusy) return;
    const rows = data.rows.filter(
      (r) => selected.has(r.invoiceId) && (filter ? filter(r) : true),
    );
    if (rows.length === 0) {
      await alert({
        title: "Sin filas elegibles",
        message:
          "Ninguna de las facturas seleccionadas cumple los requisitos para esta acción.",
      });
      return;
    }
    const verb =
      "regenerate" === kind ? "regenerar Stripe en" : "enviar a conciliación";
    const ok = await confirm({
      title: "Acción en bulk",
      message: `Ejecutar ${verb} ${rows.length} factura(s).`,
      confirmLabel: `Ejecutar (${rows.length})`,
      tone: "warning",
    });
    if (!ok) return;
    setBulkBusy(kind);
    let failures = 0;
    for (const row of rows) {
      try {
        await request(path(row), { method: "POST", token, body: {} });
      } catch {
        failures++;
      }
    }
    setBulkBusy(null);
    setSelected(new Set());
    await load();
    if (failures > 0) {
      setError(`${failures} factura(s) fallaron`);
    }
  };

  if (loading && !data) {
    return (
      <div style={{ padding: "1.5rem", color: "#73879C" }}>Cargando impagos…</div>
    );
  }

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1600, margin: "0 auto" }}>
      <div
        style={{
          marginBottom: "0.75rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "1rem",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "1.15rem", color: "#2A3F54" }}>
            Gestión de impagos
          </h2>
          <p style={{ margin: "0.25rem 0 0", color: "#73879C", fontSize: ".85rem" }}>
            Facturas rechazadas, sin reintentos disponibles o disputadas — ordenadas por
            fecha (más recientes primero).
          </p>
        </div>
        <button
          type="button"
          onClick={handleExportCsv}
          disabled={exporting || loading}
          title="Descargar un CSV con todas las facturas que coinciden con los filtros activos"
          style={{
            padding: "0.45rem 0.85rem",
            borderRadius: "0.4rem",
            background: exporting ? "#73879C" : "#0069d9",
            color: "white",
            border: `1px solid ${exporting ? "#73879C" : "#0069d9"}`,
            fontSize: ".82rem",
            fontWeight: 600,
            cursor: exporting || loading ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {exporting ? "Exportando…" : "Exportar CSV"}
        </button>
      </div>

      {data && (
        <AggregatesBar
          aggregates={data.aggregates}
          total={data.total}
          selectedStatuses={selectedStatuses}
          onToggleStatus={toggleStatus}
        />
      )}

      <FiltersBar
        search={search}
        onSearch={(v) => {
          setSearch(v);
          setPage(0);
        }}
        carrier={carrier}
        onCarrier={(v) => {
          setCarrier(v);
          setPage(0);
        }}
        carriers={carriers}
        minAge={minAge}
        onMinAge={(v) => {
          setMinAge(v);
          setPage(0);
        }}
        maxAge={maxAge}
        onMaxAge={(v) => {
          setMaxAge(v);
          setPage(0);
        }}
        paymentMethods={paymentMethods}
        onTogglePaymentMethod={togglePaymentMethod}
        minLeadUnpaidCount={minLeadUnpaidCount}
        onMinLeadUnpaidCount={(v) => {
          setMinLeadUnpaidCount(v);
          setPage(0);
        }}
        leadStatuses={leadStatuses}
        onToggleLeadStatus={toggleLeadStatus}
        onClear={clearFilters}
      />

      {selected.size > 0 && (
        <div
          style={{
            background: "#fff8e1",
            border: "1px solid #ffe69c",
            color: "#856404",
            borderRadius: "0.5rem",
            padding: "0.55rem 0.85rem",
            marginBottom: "0.65rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          <strong>{selected.size}</strong> factura(s) seleccionadas
          <button
            type="button"
            disabled={!!bulkBusy}
            onClick={() =>
              runBulk(
                "regenerate",
                (r) => `/api/admin/operations/invoices/${r.invoiceId}/regenerate-stripe`,
              )
            }
            style={bulkBtn}
          >
            {bulkBusy === "regenerate" ? "Procesando…" : "Regenerar Stripe (bulk)"}
          </button>
          <button
            type="button"
            disabled={!!bulkBusy}
            onClick={() =>
              runBulk(
                "conciliate",
                (r) => `/api/admin/operations/invoices/${r.invoiceId}/conciliate`,
                (r) => r.leadMarket === "IT" || r.leadMarket === "SQL_ES",
              )
            }
            style={bulkBtn}
          >
            {bulkBusy === "conciliate" ? "Procesando…" : "Conciliar (bulk, IT/SQL_ES)"}
          </button>
          <button
            type="button"
            disabled={!!bulkBusy || selectedUniqueLeadUlids.length === 0}
            onClick={() => setCancelFormOpen((v) => !v)}
            title={
              selectedUniqueLeadUlids.length === 0
                ? "Las facturas seleccionadas no tienen lead asociado"
                : `${selectedUniqueLeadUlids.length} lead(s) único(s) en la selección`
            }
            style={{ ...bulkBtn, background: "#b32424", borderColor: "#b32424" }}
          >
            {bulkBusy === "cancel"
              ? "Cancelando…"
              : `Cancelar por impago (${selectedUniqueLeadUlids.length})…`}
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            style={{ ...bulkBtn, background: "white", color: "#73879C" }}
          >
            Deseleccionar
          </button>
        </div>
      )}

      {cancelFormOpen && selected.size > 0 && (
        <div
          style={{
            background: "white",
            border: "1px solid #f3c4c4",
            borderLeft: "3px solid #b32424",
            borderRadius: "0.5rem",
            padding: "0.75rem 0.95rem",
            marginBottom: "0.65rem",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            gap: "0.75rem",
          }}
        >
          <div style={{ flex: "1 1 100%", marginBottom: "0.15rem" }}>
            <strong style={{ color: "#b32424" }}>
              Cancelar {selectedUniqueLeadUlids.length} lead(s) por impago
            </strong>
            <div style={{ fontSize: ".75rem", color: "#73879C" }}>
              Razón: UNPAID_INVOICES. Las mismas fechas se aplicarán a todos los leads.
            </div>
          </div>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
            <span style={{ fontSize: ".72rem", color: "#73879C", fontWeight: 600 }}>
              Fecha solicitud
            </span>
            <input
              type="date"
              value={cancelRequestDate}
              onChange={(e) => setCancelRequestDate(e.target.value)}
              style={{
                padding: "0.4rem 0.55rem",
                borderRadius: "0.4rem",
                border: "1px solid #dee2e6",
                background: "#f8f9fb",
                fontSize: ".85rem",
              }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
            <span style={{ fontSize: ".72rem", color: "#73879C", fontWeight: 600 }}>
              Fecha efectiva
            </span>
            <input
              type="date"
              value={cancelEffectiveDate}
              onChange={(e) => setCancelEffectiveDate(e.target.value)}
              style={{
                padding: "0.4rem 0.55rem",
                borderRadius: "0.4rem",
                border: "1px solid #dee2e6",
                background: "#f8f9fb",
                fontSize: ".85rem",
              }}
            />
          </label>
          <button
            type="button"
            disabled={!!bulkBusy}
            onClick={runBulkCancel}
            style={{
              ...bulkBtn,
              background: "#b32424",
              borderColor: "#b32424",
            }}
          >
            {bulkBusy === "cancel"
              ? "Cancelando…"
              : `Cancelar ${selectedUniqueLeadUlids.length} lead(s)`}
          </button>
          <button
            type="button"
            onClick={() => setCancelFormOpen(false)}
            style={{ ...bulkBtn, background: "white", color: "#73879C" }}
          >
            Cerrar
          </button>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "0.6rem 0.85rem",
            background: "#fde2e2",
            color: "#b32424",
            borderRadius: "0.45rem",
            marginBottom: "0.65rem",
            fontSize: ".82rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.6rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "hidden",
          opacity: loading ? 0.6 : 1,
          transition: "opacity .15s ease",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }}>
            <thead>
              <tr style={{ background: "#f8f9fb" }}>
                <Th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={
                      data !== null
                      && data.rows.length > 0
                      && selected.size === data.rows.length
                    }
                    onChange={selectAll}
                    aria-label="Seleccionar todo"
                  />
                </Th>
                <Th>Edad</Th>
                <Th>Status</Th>
                <Th>Factura</Th>
                <Th>Lead</Th>
                <Th>Market</Th>
                <Th>Carrier</Th>
                <Th>Pago</Th>
                <Th>Suscripción</Th>
                <Th style={{ textAlign: "right" }}>Importe</Th>
                <Th style={{ textAlign: "right" }}>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {data === null || data.rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    style={{ padding: "1rem", color: "#73879C", textAlign: "center" }}
                  >
                    Sin impagos para los filtros aplicados
                  </td>
                </tr>
              ) : (
                data.rows.map((row) => (
                  <Row
                    key={row.invoiceId}
                    row={row}
                    selected={selected.has(row.invoiceId)}
                    onSelect={() => toggleSelect(row.invoiceId)}
                    onReload={load}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {data && data.total > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data.total}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
            disabled={loading}
          />
        )}
      </div>
    </div>
  );
}

function Row({
  row,
  selected,
  onSelect,
  onReload,
}: {
  row: UnpaidInvoiceRow;
  selected: boolean;
  onSelect: () => void;
  onReload: () => void;
}) {
  const statusStyle =
    UNPAID_STATUS_STYLES[row.status as UnpaidStatus] ?? {
      bg: "#e9ecef",
      fg: "#495057",
    };
  return (
    <tr
      style={{
        borderBottom: "1px solid #f1f3f5",
        background: selected ? "#fff8e1" : "white",
      }}
    >
      <Td>
        <input type="checkbox" checked={selected} onChange={onSelect} />
      </Td>
      <Td>
        <span
          style={{
            fontVariantNumeric: "tabular-nums",
            color: ageColor(row.ageDays),
            fontWeight: 700,
          }}
        >
          {row.ageDays === null ? "—" : `${row.ageDays}d`}
        </span>
        <div style={{ fontSize: ".7rem", color: "#73879C" }}>
          {fmtDate(row.invoiceDate)}
        </div>
      </Td>
      <Td>
        <span
          style={{
            display: "inline-block",
            padding: "0.15rem 0.5rem",
            borderRadius: "999px",
            background: statusStyle.bg,
            color: statusStyle.fg,
            fontSize: ".7rem",
            fontWeight: 700,
            letterSpacing: ".3px",
            whiteSpace: "nowrap",
          }}
        >
          {UNPAID_STATUS_LABEL[row.status as UnpaidStatus] ?? row.status}
        </span>
      </Td>
      <Td mono>
        <div>
          A{row.annuality ?? "?"} #{row.invoiceNumber ?? "?"}
        </div>
        <div style={{ fontSize: ".68rem", color: "#73879C" }}>{row.invoiceId}</div>
      </Td>
      <Td>
        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
          {row.leadUlid ? (
            <Link
              to={`/leads/${row.leadUlid}`}
              style={{ color: "#0069d9", textDecoration: "none", fontWeight: 600 }}
            >
              {row.leadEmail ?? row.leadName ?? `Lead ${row.leadId}`}
            </Link>
          ) : (
            row.leadEmail ?? row.leadName ?? "—"
          )}
          {row.leadUnpaidCount > 1 && (
            <span
              title={`${row.leadUnpaidCount} impagos en este lead`}
              style={{
                display: "inline-block",
                padding: "0.05rem 0.35rem",
                borderRadius: "999px",
                fontSize: ".65rem",
                fontWeight: 700,
                background: row.leadUnpaidCount >= 3 ? "#7c1d1d" : "#fff3cd",
                color: row.leadUnpaidCount >= 3 ? "white" : "#856404",
                lineHeight: 1.4,
              }}
            >
              ×{row.leadUnpaidCount}
            </span>
          )}
        </div>
        <div style={{ fontSize: ".7rem", color: "#73879C" }}>
          {row.policyNumber ?? `lead#${row.leadId ?? "—"}`}
        </div>
      </Td>
      <Td mono>{row.leadMarket ?? "—"}</Td>
      <Td>{row.carrier ?? "—"}</Td>
      <Td>
        {row.paymentMethodType ? (
          <span
            style={{
              display: "inline-block",
              padding: "0.1rem 0.45rem",
              borderRadius: "0.3rem",
              fontSize: ".72rem",
              fontWeight: 600,
              background: PAYMENT_METHOD_STYLES[row.paymentMethodType].bg,
              color: PAYMENT_METHOD_STYLES[row.paymentMethodType].fg,
            }}
          >
            {PAYMENT_METHOD_LABEL[row.paymentMethodType]}
          </span>
        ) : (
          "—"
        )}
        {row.paymentMethodLast4 && (
          <span style={{ marginLeft: "0.3rem", fontSize: ".72rem", color: "#6c757d" }}>
            ····{row.paymentMethodLast4}
          </span>
        )}
      </Td>
      <Td>
        {row.subscriptionId ? (
          <Link
            to={`/subscriptions/${row.subscriptionId}`}
            style={{
              color: "#0069d9",
              textDecoration: "none",
              fontSize: ".78rem",
              fontFamily: "monospace",
            }}
            title="Abrir suscripción"
          >
            {row.subscriptionStatus ?? "—"}
            <div style={{ fontSize: ".68rem", color: "#73879C" }}>
              {row.subscriptionId}
            </div>
          </Link>
        ) : (
          "—"
        )}
      </Td>
      <Td mono style={{ textAlign: "right" }}>
        {fmtMoney(row.finalPrice ?? row.rawPrice)}
      </Td>
      <Td style={{ textAlign: "right" }}>
        <ImpagoActionsMenu row={row} onReload={onReload} />
      </Td>
    </tr>
  );
}

function Th({
  children,
  style,
}: {
  children?: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <th
      style={{
        padding: "0.55rem 0.75rem",
        textAlign: "left",
        fontSize: ".7rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".4px",
        color: "#73879C",
        borderBottom: "1px solid #e9ecef",
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  mono,
  style,
}: {
  children: React.ReactNode;
  mono?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <td
      style={{
        padding: "0.55rem 0.75rem",
        fontFamily: mono ? "monospace" : "inherit",
        color: "#1f2933",
        verticalAlign: "middle",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

const bulkBtn: React.CSSProperties = {
  padding: "0.3rem 0.7rem",
  borderRadius: "0.35rem",
  background: "#856404",
  color: "white",
  border: "1px solid #856404",
  fontSize: ".75rem",
  fontWeight: 600,
  cursor: "pointer",
};
